import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { nanoid } from 'nanoid';
import styled from 'styled-components';
import { Button, Input } from 'antd';

import fontService, { FontWeight } from 'services/FontService';
import { SkillIcon } from 'atoms/SkillIcon/SkillIcon';
import { desktop, tablet } from 'services/ui';
import { removeLabel } from 'services/removeLabel';
import { Colors, ICareerProjectValue } from 'context';
import { ICareerSkillOption } from 'core';
import { Icon, IconNames } from 'atoms';
import { SkillAutocomplete } from 'molecules';
import { FormFieldset } from 'organisms/ResumeForm';

const { TextArea } = Input;

export interface IProfileProjectsBlock {
  projects: ICareerProjectValue[];
  onChange: (projects: ICareerProjectValue[]) => void;
}

const emptyProject = (): ICareerProjectValue => ({
  id: nanoid(),
  title: '',
  description: '',
  role: '',
  repoUrl: '',
  demoUrl: '',
  technologies: [],
});

/**
 * Portfolio projects as a TOP-LEVEL section (spec 0018 §3.1) — closes the gap
 * where a junior with no work experience could not show pet projects.
 * Technologies are picked from the skill dictionary (autocomplete -> technologyIds).
 */
export const ProfileProjectsBlock = memo(({ projects, onChange }: IProfileProjectsBlock) => {
  const { t } = useTranslation();

  const patch = useCallback(
    (id: string, part: Partial<ICareerProjectValue>) => {
      onChange(projects.map((project) => (project.id === id ? { ...project, ...part } : project)));
    },
    [projects, onChange],
  );

  const onAddTechnology = useCallback(
    (id: string, option: ICareerSkillOption) => {
      const project = projects.find((item) => item.id === id);
      if (!project || project.technologies.some((tech) => tech.id === option.id)) {
        return;
      }
      // 🔴 Слаг, категория и картинка сохраняются вместе с идентификатором.
      // Раньше здесь оставались только id и имя — и знак у только что
      // добавленной технологии появлялся лишь после перезагрузки страницы,
      // когда её заново приносил сервер.
      patch(id, {
        technologies: [
          ...project.technologies,
          {
            id: option.id,
            name: option.name,
            slug: option.slug,
            category: option.category,
            iconUrl: option.iconUrl,
          },
        ],
      });
    },
    [projects, patch],
  );

  const onRemoveTechnology = useCallback(
    (id: string, techId: string) => {
      const project = projects.find((item) => item.id === id);
      if (!project) {
        return;
      }
      patch(id, { technologies: project.technologies.filter((tech) => tech.id !== techId) });
    },
    [projects, patch],
  );

  const onRemoveProject = useCallback(
    (id: string) => onChange(projects.filter((project) => project.id !== id)),
    [projects, onChange],
  );

  const onAddProject = useCallback(
    () => onChange([...projects, emptyProject()]),
    [projects, onChange],
  );

  return (
    <FormFieldset groupTitle={t('careerProfile.projects.title')}>
      <List>
        {projects.map((project) => (
          <Card key={project.id}>
            <RemoveButton
              type="button"
              aria-label={removeLabel(t, 'careerProfile.remove.project', project.title)}
              onClick={() => onRemoveProject(project.id)}>
              <StyledCloseIcon name={IconNames.CloseSm} />
            </RemoveButton>

            <Label>{t('careerProfile.projects.projectTitle')}</Label>
            <Input
              value={project.title}
              maxLength={150}
              onChange={({ target: { value } }) => patch(project.id, { title: value })}
            />

            <TwoCol>
              <div>
                <Label>{t('careerProfile.projects.role')}</Label>
                <Input
                  value={project.role}
                  onChange={({ target: { value } }) => patch(project.id, { role: value })}
                />
              </div>
              <div>
                <Label>{t('careerProfile.projects.repoUrl')}</Label>
                <Input
                  value={project.repoUrl}
                  onChange={({ target: { value } }) => patch(project.id, { repoUrl: value })}
                />
              </div>
            </TwoCol>

            <Label>{t('careerProfile.projects.demoUrl')}</Label>
            <Input
              value={project.demoUrl}
              onChange={({ target: { value } }) => patch(project.id, { demoUrl: value })}
            />

            <Label>{t('careerProfile.projects.description')}</Label>
            <TextArea
              value={project.description}
              rows={4}
              maxLength={1500}
              onChange={({ target: { value } }) => patch(project.id, { description: value })}
            />

            <Label>{t('careerProfile.projects.technologies')}</Label>
            <SkillAutocomplete
              placeholder={t('careerProfile.projects.technologiesPlaceholder')}
              onSelectSkill={(option) => onAddTechnology(project.id, option)}
            />
            <Tags>
              {project.technologies.map((tech) => (
                // 0073: своя кнопка вместо Tag closable — у antd крестик не
                // фокусируется и озвучивается «close» по-английски.
                <TechTag key={tech.id}>
                  <SkillIcon
                    slug={tech.slug}
                    name={tech.name}
                    category={tech.category}
                    iconUrl={tech.iconUrl}
                    size={16}
                  />
                  {tech.name}
                  <TechRemove
                    type="button"
                    aria-label={removeLabel(t, 'careerProfile.remove.technology', tech.name)}
                    onClick={() => onRemoveTechnology(project.id, tech.id)}>
                    <span aria-hidden>×</span>
                  </TechRemove>
                </TechTag>
              ))}
            </Tags>
          </Card>
        ))}
      </List>

      <Button onClick={onAddProject}>{t('careerProfile.projects.addProject')}</Button>
    </FormFieldset>
  );
});

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
`;

const Card = styled.div`
  position: relative;
  background-color: ${Colors.bg_gray};
  border-radius: 8px;
  padding: 20px 20px 28px;

  ${tablet} {
    padding: 24px;
  }

  ${desktop} {
    padding: 32px;
  }
`;

const Label = styled.p`
  margin: 16px 0 4px;
  ${fontService.text({ size: 14, lineHeight: 145, weight: FontWeight.medium })}

  &:first-of-type {
    margin-top: 0;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  ${tablet} {
    grid-template-columns: 1fr 1fr;
  }
`;

const Tags = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const RemoveButton = styled.button`
  cursor: pointer;
  border-radius: 50%;
  border: none;
  background-color: ${Colors.white};
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 16px rgba(100, 100, 100, 0.2);
  position: absolute;
  top: -15px;
  right: -14px;
  z-index: 3;
  padding: 0;
`;

const StyledCloseIcon = styled(Icon)`
  width: 20px;
  height: 20px;
`;

const TechTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 0 0 10px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 8px;
  background: ${Colors.white};
  ${fontService.text({ size: 14, lineHeight: 145, weight: FontWeight.regular })}
`;

const TechRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${Colors.gray_dark};
  cursor: pointer;
  font-size: 18px;
  line-height: 1;

  &:hover {
    color: ${Colors.black};
  }

  &:focus-visible {
    outline: 2px solid ${Colors.black};
    outline-offset: -2px;
  }
`;
